import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    View
} from "react-native";
import type IFieldProps from "./type";
import type {
    FieldResolvedOrientation,
    FieldRegisteredState,
    FieldContextType,
    FieldPartKind
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import FieldContent from "./components/content";
import FieldDescription from "./components/description";
import FieldError from "./components/error";
import FieldGroup from "./components/group";
import FieldLegend from "./components/legend";
import FieldSet from "./components/set";
import FieldTitle from "./components/title";
import {
    useFieldGroupContext,
    FieldContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

const Field: FC<IFieldProps> = ({
    customTheme: customThemeProp,
    orientation: orientationProp,
    isInvalid: isInvalidProp,
    onLayout: onLayoutProp,
    isDisabled = false,
    children,
    style,
    ...props
}) => {
    const groupContext = useFieldGroupContext();

    const customTheme = customThemeProp ?? groupContext?.customTheme;

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        registeredState,
        setRegisteredState
    ] = useState<FieldRegisteredState>({});

    const [
        containerWidth,
        setContainerWidth
    ] = useState<number>(0);

    const [
        isSettled,
        setIsSettled
    ] = useState<boolean>(false);

    const registeredRef = useRef<FieldRegisteredState>({});

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const titleID = `${baseID}-title`;

    const descriptionID = `${baseID}-description`;

    const errorID = `${baseID}-error`;

    const currentOrientation = orientationProp ?? groupContext?.orientation ?? "vertical";

    const isNarrow = containerWidth > 0 && containerWidth < shapeConfigs.compactBreakpoint;

    const orientation: FieldResolvedOrientation = currentOrientation === "responsive" ? isNarrow ? "vertical" : "horizontal" : currentOrientation;

    const hasError = Boolean(registeredState.error);

    const isInvalid = isInvalidProp ?? hasError;

    const {
        container: containerDynamicStyle
    } = useStyles({
        orientation,
        spaces
    });

    const registerPart = useCallback((part: FieldPartKind, text?: string) => {
        registeredRef.current = {
            ...registeredRef.current,
            [part]: {
                text
            }
        };

        setRegisteredState(registeredRef.current);

        return () => {
            registeredRef.current = {
                ...registeredRef.current,
                [part]: undefined
            };

            setRegisteredState(registeredRef.current);
        };
    }, []);

    useEffect(() => {
        setIsSettled(true);
    }, []);

    const handleLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(Math.floor(event.nativeEvent.layout.width));

        onLayoutProp?.(event);
    };

    const context = useMemo<FieldContextType>(() => ({
        hasDescription: Boolean(registeredState.description),
        descriptionText: registeredState.description?.text,
        hasTitle: Boolean(registeredState.title),
        errorText: registeredState.error?.text,
        titleText: registeredState.title?.text,
        descriptionID,
        registerPart,
        customTheme,
        orientation,
        isDisabled,
        isInvalid,
        isSettled,
        hasError,
        titleID,
        errorID
    }), [
        registeredState,
        descriptionID,
        registerPart,
        orientation,
        customTheme,
        isDisabled,
        isInvalid,
        isSettled,
        hasError,
        titleID,
        errorID
    ]);

    return <FieldContext.Provider
        value={context}
    >
        <View
            {...props}
            aria-disabled={isDisabled ? true : undefined}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            onLayout={handleLayout}
            role="group"
        >
            {children}
        </View>
    </FieldContext.Provider>;
};
export default Object.assign(Field, {
    Description: FieldDescription,
    Content: FieldContent,
    Legend: FieldLegend,
    Error: FieldError,
    Group: FieldGroup,
    Title: FieldTitle,
    Set: FieldSet
});
